import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import updateShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/UpdateShipTo";
import {
    getCurrentShipToState,
    getShipTosDataView,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import loadShipTos from "@insite/client-framework/Store/Pages/Addresses/Handlers/LoadShipTos";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    BillToModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Icon from "@insite/mobius/Icon";
import EditIcon from "@MasterBluePrint/Icons/EditIcon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CustomerAddressForm from "@MasterBluePrint/Overrides/Components/CustomerAddressForm"
import * as React from "react";
import { createContext, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    shipTosDataView: getShipTosDataView(state, state.pages.addresses.getShipTosParameter),
    getShipTosParameter: state.pages.addresses.getShipTosParameter,
    currentBillTo: getCurrentBillToState(state).value,
    currentShipTo: getCurrentShipToState(state).value,
    countries: getCurrentCountries(state),
    addressFieldDataView: getAddressFieldsDataView(state),
});

const mapDispatchToProps = {
    setCurrentShipTo,
    loadShipTos,
    updateShipTo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AddressBookStyles {
    
    editLink?: LinkPresentationProps;
    deleteLink?: LinkPresentationProps;
    gridContainer?: GridContainerProps;
    titleTextGridItem?: GridItemProps;
    addressHeaderGridItem?: GridItemProps;
    titleText?: TypographyProps;
    noAddressesText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    addressCardGridItem?: GridItemProps;
    addressFormModal?: ModalPresentationProps;
    addressCard?: AddressCardStyles;
    addressFormModalCustom?: InjectableCss;
    closeContainer?: InjectableCss;
    
}

interface AddressCardStyles {
    gridContainer?: GridContainerProps;
    addressInfoDisplayGridItem?: GridItemProps;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    actionsGridItem?: GridItemProps;
    actionOverflowIcon?: IconPresentationProps;
    actionUseAsShippingAddressButton?: LinkPresentationProps;
    actionEditLink?: LinkPresentationProps;
    bottomBorderGridItem?: GridItemProps;
    bottomBorder?: InjectableCss;
    overflowMenu?: OverflowMenuPresentationProps;
    
}

export const addressBookStyles: AddressBookStyles = {
    closeContainer: {
        css: css`
            position: absolute;
            left: 1.8rem;
            top: -5rem;
            z-index: 9;
            cursor: pointer;
            .close-label {
                color: ${themeColors.colorBrandsSeeMore};
                font-family: Roboto;
                font-size: 0.875rem;
                letter-spacing: 0;
                line-height: 1;
                display: inline-block;
                margin-right: 0.525rem;
                vertical-align: middle;
            }
            .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1rem;
                height: 1rem;
                svg {
                    height: 1rem;
                    width: 1rem;
                    color: #535e64;
                }
            }
            @media screen and (max-width: 990px) {
                right: 1.25rem;
            }
        `,
    },
    editLink: {
        css: css`
            margin-left: 0.9375rem;
            padding-left: 1.25rem;
            border-left: 1px solid ${themeColors.colorNeonSilver};

            svg {
                width: 1.25rem;
                height: 1.25rem;
            }
        `,
    },
    deleteLink: {
        css: css`
            svg {
                width: 1.25rem;
                height: 1.25rem;
                color: ${themeColors.colorBattleshipGray};
            }
        `,
    },
    gridContainer: { gap: 30 },
    titleTextGridItem: { width: 12},
    addressHeaderGridItem: { 
        width: [12],
        css:css`
            padding: 0 1.25rem 1.25rem;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [   
                        null,
                        null, 
                        css`
                            padding-bottom: 0.625rem;
                        `,
                        null,
                    ],
                    "max",
                )
            };

            
        `,
    },
    titleText: { 
        variant: "h5",
        css:css`
        position: relative;
            margin-left: 0.625rem;        
            font-size: 1.375rem;
            padding-bottom: 0.3125rem;
            &:after {
                height: 0.19rem;
                width: 2.5rem;
                background: ${themeColors.colorRed};
                content: "";
                display: inline-block;
                position: absolute;
                bottom: 0;
                left: 0.0625rem;
            }
        `,
    },
    noAddressesText: {
        variant: "h4",
        css: css`
            display: block;
            margin: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    addressCardGridItem: { 
        width: [12,12,6,4,4],
        css:css`  
            padding: 0.9375rem;

            > div {
                border-radius: 0.125rem;   
                background-color: ${themeColors.colorWhite};
                box-shadow: 0 0.125rem 0.25rem 0.25rem rgba(0,0,0,0.03);
                padding: 2.1875rem 1.0625rem 1.25rem 2rem;
                display: flex;
                height:100%;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null, 
                        css`
                            padding-top: 0.625rem; 
                            padding-bottom: 0.625rem;
                        `,
                        null,
                    ],
                    "max",
                )
            };
        `, 
    },
    addressCard: {
        
        gridContainer: { gap: 5 },
        addressInfoDisplayGridItem: { 
            width: [10, 10, 6, 10, 10],
            css:css`
                padding: 0.625rem 0.625rem 1.25rem;
                font-family: 'Lato';
                color: ${themeColors.colorSquidInk};
                p {
                    font-size: 1rem;
                }

                address {
                    span[data-test-selector="addressInfoDisplay_firstName"],
                    span[data-test-selector="addressInfoDisplay_lastName"] {
                        font-size: 1rem;
                        color: ${themeColors.colorInfo};
                        font-weight: ${themeFontWeights.fontWeightForBold}; 
                    }
                }
            `, 
        },
        actionsGridItem: {
            width: [12],
            css: css`
                position: relative;
                align-items: center;
                justify-content: space-between;
                display: flex;
                width:100%;
                padding: 0.625rem 0;
                border-top: 1px solid ${themeColors.colorBorderBrands};
                margin: auto 0.625rem 0 !important;
                
            `,
        },
        actionUseAsShippingAddressButton: {
        css:css`
            span[class*="StyledTypography"] {
                color: ${themeColors.colorBattleshipGray};
                font-family: Lato;
                font-size: 0.9375rem;
                letter-spacing: 0.17px;
                line-height: 1.125rem;
                text-decoration: underline;
                text-transform: initial;

                &:hover {
                    color: ${themeColors.colorBattleshipGray};
                }
            }
        `,
        },
        actionEditLink: {
            css: css`
                display: inline-block;
                margin: 0;
                position: absolute;
                right: 0;
            `,
        },
        
        bottomBorderGridItem: { width: 12 },
        bottomBorder: {
            css: css`
                height: 1px;
                background-color: ${themeColors.colorBorderBrands};
                width: 100%;
            `,
        },
        overflowMenu: {
            cssOverrides: {
                menu: css`
                    width: 13.125rem;
                `,
            },
        },
    },
    addressFormModal: { sizeVariant: "medium" },
    addressFormModalCustom: {
        css:css`
            div[class^='ModalContent'], 
            div[class*=' ModalContent']{
                position: relative;
                padding-bottom: 11.1875rem !important;
                height: calc(100vh - 13.1875rem);

                @media screen and (max-width: 767px) {
                    padding-bottom: 10.1875rem !important;  
                    height: calc(100vh - 13.1875rem);
                }

                @media screen and (max-width: 540px) { 
                    height: calc(100vh - 11.8rem);
                    padding-bottom: 11.8rem !important;
                }
            }
            
            > div {
                max-height: 100vh;
                margin: 0 0 0 auto;
                max-width: 35.625rem;
                border: 1px solid rgba(7,15,44,0.08);
                border-radius: 1px;
                background-color: ${themeColors.colorWhite};

                @media (max-width: 767px) {
                    max-width: 100%;
                    margin-right: 0;
                    margin-left: 0;
                }

                > div:last-child {
                    padding: 0;
                    > form {
                        > div {
                            padding: 1.875rem;
                        }

                        > div:last-child {
                            border-radius: 1px;
                            background: ${themeColors.colorWhite};
                            box-shadow: 0 -0.125rem 0.375rem 0 rgba(0,0,0,0.31);
                            padding-left: 0;
                            padding-right: 0;

                            @media screen and (max-width: 767px) {
                                box-shadow: none;
                            }
                        }
                        @media screen and (max-width: 326px) {
                            > div {
                                padding: 1.875rem 1.875rem 2.2rem;                           
                            }
                            > div:last-child {
                                padding-top: 7.5rem;
                                padding-bottom: 3.875rem;
                            }                            
                        }
                        @media screen and (min-width: 360px) and (max-width: 369px) {                            
                            > div:last-child {
                                padding-top: 5rem;
                            }                            
                        }
                        @media screen and (min-width: 370px) and (max-width: 379px) {                          
                            > div:last-child {
                                padding-top: 2.8rem;
                            }                            
                        }
                        @media screen and (min-width: 380px) and (max-width: 414px) {                        
                            > div:last-child {
                                padding-top: 3rem;
                            }                            
                        }
                    }
                }
                #modalTitle{
                    border-bottom: none;
                    padding-bottom: 0px;
                        h4 {
                            color: ${themeColors.colorPrimary};
                            font-family: Roboto;
                            font-size: 1.125rem;
                            font-weight: ${themeFontWeights.fontWeightMedium};
                            letter-spacing: 0;
                            line-height: 1.9375rem;
                            padding-top: 4.0625rem;
                            position: relative;
                            &:after {
                                height: 0.1875rem;
                                width: 2.75rem;
                                background: ${themeColors.colorRed};
                                content: "";
                                display: inline-block;
                                position: absolute;
                                bottom: -0.1875rem;
                                left: 1px;
                            }
                        }
                        button[aria-labelledby="close-modal"] {
                            display: none;
                        }
                    }
                }
        `,
    },
};

const styles = addressBookStyles;

type ContextType = {
    isSettingShipTo: boolean;
    setIsSettingShipTo: (value: boolean) => void;
};

export const AddressBookContext = createContext<ContextType>({
    isSettingShipTo: false,
    setIsSettingShipTo: value => {},
});

const AddressBook: React.FC<Props> = ({
    id,
    shipTosDataView,
    loadShipTos,
    setCurrentShipTo,
    updateShipTo,
    addressFieldDataView,
    countries,
    currentBillTo,
    currentShipTo,
    getShipTosParameter,
}) => {
    const [isSettingShipTo, setIsSettingShipTo] = useState(false);

    React.useEffect(() => {
        if (!shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos();
        }
    });
    const setCurrentShipToHandler = (e:any) => {
        setCurrentShipTo(e);
        loadShipTos();
    };

    let component;
    if (shipTosDataView.isLoading || !shipTosDataView.value || !addressFieldDataView.value) {
        component = (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    } else if (shipTosDataView.value.length > 0) {
        component = shipTosDataView.value.map(shipTo => (
            <GridItem {...styles.addressCardGridItem} key={shipTo.id.toString()}>
                <AddressCard
                    shipTo={shipTo}
                    setCurrentShipTo={setCurrentShipToHandler}
                    updateShipTo={updateShipTo}
                    billToAddressFields={addressFieldDataView.value!.billToAddressFields}
                    shipToAddressFields={addressFieldDataView.value!.shipToAddressFields}
                    countries={countries}
                    currentBillTo={currentBillTo}
                    currentShipTo={currentShipTo}
                />
            </GridItem>
        ));
    } else {
        component = (
            <StyledWrapper {...styles.centeringWrapper}>
                <Typography {...styles.noAddressesText}>
                    {getShipTosParameter.filter
                        ? siteMessage("Addresses_NoResultsFound")
                        : siteMessage("Addresses_NoAddressesFound")}
                </Typography>
            </StyledWrapper>
        );
    }

    return (
        <AddressBookContext.Provider value={{ isSettingShipTo, setIsSettingShipTo }}>
            <GridContainer {...styles.gridContainer} className="addressBookContent">
                {component}
            </GridContainer>
        </AddressBookContext.Provider>
    );
};

interface AddressCardProps {
    shipTo: ShipToModel;
    currentBillTo?: BillToModel;
    currentShipTo?: ShipToModel;
    countries?: CountryModel[];
    billToAddressFields?: AddressFieldDisplayCollectionModel;
    shipToAddressFields?: AddressFieldDisplayCollectionModel;
    setCurrentShipTo: Props["setCurrentShipTo"];
    updateShipTo: Props["updateShipTo"];
}

const AddressCard: React.FunctionComponent<AddressCardProps> = (props: AddressCardProps) => {
   
    const { shipTo } = props;

  
    const componentStyles: AddressCardStyles = styles.addressCard || {};

    return (
        <GridContainer {...componentStyles.gridContainer} data-test-selector="shipping_addresses_container">
            <GridItem {...componentStyles.addressInfoDisplayGridItem}>
                <AddressInfoDisplay
                    firstName={shipTo.firstName}
                    lastName={shipTo.lastName}
                    companyName={shipTo.companyName}
                    attention={shipTo.attention}
                    address1={shipTo.address1}
                    address2={shipTo.address2}
                    address3={shipTo.address3}
                    address4={shipTo.address4}
                    city={shipTo.city}
                    state={shipTo.state ? shipTo.state.abbreviation : undefined}
                    postalCode={shipTo.postalCode}
                    country={shipTo.country ? shipTo.country.abbreviation : undefined}
                    phone={shipTo.phone}
                    fax={shipTo.fax}
                    email={shipTo.email}
                    extendedStyles={componentStyles.addressInfoDisplay}
                />
            </GridItem>
            
           
            <GridItem {...componentStyles.actionsGridItem} data-test-selector="addressActionGrid">
                <AddressActions
                    shipTo={shipTo}
                    updateShipTo={props.updateShipTo}
                    setCurrentShipTo={props.setCurrentShipTo}
                    currentBillTo={props.currentBillTo}
                    currentShipTo={props.currentShipTo}
                    countries={props.countries}
                    shipToAddressFields={props.shipToAddressFields}
                    billToAddressFields={props.billToAddressFields}
                />
            </GridItem>
        </GridContainer>
    );
};

interface AddressActionsProps {
    shipTo: ShipToModel;
    currentBillTo?: BillToModel;
    currentShipTo?: ShipToModel;
    countries?: CountryModel[];
    billToAddressFields?: AddressFieldDisplayCollectionModel;
    shipToAddressFields?: AddressFieldDisplayCollectionModel;
    setCurrentShipTo: Props["setCurrentShipTo"];
    updateShipTo: Props["updateShipTo"];
}

const AddressActions: React.FunctionComponent<AddressActionsProps> = (props: AddressActionsProps) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [deleteModalIsOpen, setDeleteModalIsOpen] = React.useState(false);
    const { shipTo, currentShipTo } = props;

    const shipToData=  { ...shipTo };
    shipToData.isDefault=shipTo.id === currentShipTo?.id;
    const addressBookContext = useContext(AddressBookContext);

    const useAsShippingAddressHandler = (e: any) => {
        e.preventDefault();
        addressBookContext.setIsSettingShipTo(true);
        props.setCurrentShipTo({
            shipToId: shipTo.id,
            onSuccess: () => {
                addressBookContext.setIsSettingShipTo(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
        setDeleteModalIsOpen(false);       
    };

    const closeDeleteModelhandler=()=>{
        setDeleteModalIsOpen(false);
        window.location.reload();
    };

    const editClickHandler = () => {
        setModalIsOpen(true);
    };

    const formCancelHandler = (e: any) => {
        e.preventDefault();
        modalCloseHandler();
    };

    const formSubmitHandler = (e: any, address: BaseAddressModel) => {
        e.preventDefault();
        const { currentBillTo, shipTo } = props;
        if (!currentBillTo || !shipTo) {
            return;
        }

        props.updateShipTo({
            billToId: currentBillTo.id,
            shipTo: {
                ...shipTo,
                ...address,
            },
            onComplete: result => {
                if (result.apiResult && result.apiParameter?.shipTo.isDefault){
                    addressBookContext.setIsSettingShipTo(true);
                    props.setCurrentShipTo({
                        shipToId: result.apiResult.id,
                        onSuccess: () => {
                            addressBookContext.setIsSettingShipTo(false);
                        },
                        onComplete(resultProps) {
                            if (resultProps.apiResult) {
                                this.onSuccess?.();
                            }
                        },
                    });
                }
                modalCloseHandler();
               
           }, 
        });
        // modalCloseHandler();
    };
    const deleteHandler = () => { setDeleteModalIsOpen(true); }; 
    const deleteSubmitHandler = () => {
        const { currentBillTo, shipTo } = props;
        if (!currentBillTo?.id) {
            return;
        }
        props.updateShipTo({
            billToId: currentBillTo.id,
            shipTo: { ...shipTo, },
            additionalQueryStringParameters: { canDelete: true },
        });
        closeDeleteModelhandler();
    };

    const { countries, shipToAddressFields } = props;
    const componentStyles: AddressCardStyles = styles.addressCard || {};
    return (
        <>
             
            <Link
                {...componentStyles.actionUseAsShippingAddressButton}
                onClick={useAsShippingAddressHandler}
                disabled={addressBookContext.isSettingShipTo || shipTo.id === currentShipTo?.id}
                
            >
                {translate("Use as shipping address ")}
            </Link>
            <Typography as="div">
                {shipTo.properties?.isErpNumberNotAvailable === "true" && (
                    <Link
                        {...styles.deleteLink}
                        onClick={deleteHandler}
                        data-test-selector="address_delete"
                    >
                        <Icon src="Trash2" />
                        <VisuallyHidden>{translate("Delete")}</VisuallyHidden>
                    </Link>
                )}
                
                <Link
                    {...styles.editLink}
                    onClick={editClickHandler}
                    data-test-selector="address_edit"
                >
                    <Icon src={EditIcon} />
                    <VisuallyHidden>{translate("Edit")}</VisuallyHidden>
                </Link>
            </Typography>

            <Modal
                {...styles.addressFormModal}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
                headline={translate("Edit shipping information address")}
                {...styles.addressFormModalCustom}
            >
                <Typography as="div" onClick={modalCloseHandler} {...styles.closeContainer}>
                    <span className="close-label">Close</span>
                    <Icon className="icon-close" src="XCircle" />
                </Typography>
                {countries && shipToAddressFields && (
                    <CustomerAddressForm
                        address={shipToData}
                        countries={countries}
                        addressFieldDisplayCollection={shipToAddressFields}
                        onCancel={formCancelHandler}
                        onSubmit={formSubmitHandler}
                    />
                )}
            </Modal>
            <TwoButtonModal headlineText={translate("Delete Addresses")}
             // {...styles.deleteListModal}                    
             modalIsOpen={deleteModalIsOpen} 
             messageText={translate("Are you sure you want to delete this address")}
             cancelButtonText={translate("Cancel")}
             submitButtonText={translate("Delete")} 
             onCancel={modalCloseHandler} 
             onSubmit={deleteSubmitHandler} 
             submitTestSelector="submitDeleteList">

             </TwoButtonModal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AddressBook),
    definition: {
        group: "Addresses",
        allowedContexts: ["AddressesPage"],
    },
};

export default widgetModule;
